import React, { useState, useEffect } from 'react';

const PUSH_DISMISSED_KEY = 'felgocar_push_dismissed';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 dni

const PushConsent: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const [subscribing, setSubscribing] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        // Nie pokazuj jeśli przeglądarka nie obsługuje push
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
        // Nie pokazuj jeśli już zezwolił/zablokował
        if (Notification.permission === 'granted' || Notification.permission === 'denied') return;
        // Nie pokazuj jeśli klikał "Nie teraz" w ciągu ostatnich 30 dni
        const dismissedAt = localStorage.getItem(PUSH_DISMISSED_KEY);
        if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_DURATION_MS) return;

        // Pokaż baner po 8 sekundach
        const timer = setTimeout(() => setVisible(true), 8000);
        return () => clearTimeout(timer);
    }, []);

    const handleAllow = async () => {
        setSubscribing(true);
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                setVisible(false);
                localStorage.setItem(PUSH_DISMISSED_KEY, String(Date.now()));
                return;
            }
            const reg = await navigator.serviceWorker.register('/sw.js');
            await navigator.serviceWorker.ready;
            const keyResp = await fetch('/api/push/vapid-public-key');
            const { key } = await keyResp.json();
            const pad = key.replace(/-/g, '+').replace(/_/g, '/');
            const raw = atob(pad);
            const appKey = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) appKey[i] = raw.charCodeAt(i);
            const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
            await fetch('/api/push/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ subscription: sub, userType: 'customer' })
            });
            setDone(true);
            setTimeout(() => setVisible(false), 2500);
        } catch (e) {
            console.warn('Push subscribe error:', e);
            setVisible(false);
        } finally {
            setSubscribing(false);
        }
    };

    const handleDismiss = () => {
        setVisible(false);
        localStorage.setItem(PUSH_DISMISSED_KEY, String(Date.now()));
    };

    if (!visible) return null;

    return (
        <div
            className="fixed inset-x-2 top-2 z-[9999] sm:left-0 sm:right-0 sm:top-0"
            style={{ animation: 'slideDownFade 0.4s ease' }}
        >
            <style>{`
                @keyframes slideDownFade {
                    from { opacity: 0; transform: translateY(-100%); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
            <div className="mx-auto flex max-w-[520px] items-center gap-2 rounded-xl bg-amber-400 px-3 py-2 shadow-lg ring-1 ring-black/10 sm:max-w-none sm:rounded-none sm:px-4 sm:py-3">
                <div className="flex-shrink-0 text-base sm:text-xl">🔔</div>
                <div className="flex-1 min-w-0">
                    {done ? (
                        <p className="text-xs font-bold leading-snug text-black sm:text-sm">✅ Zapisano! Będziesz informowany o promocjach.</p>
                    ) : (
                        <p className="text-xs font-bold leading-snug text-black sm:text-sm">
                            Włącz powiadomienia o nowych felgach i promocjach.
                        </p>
                    )}
                </div>
                {!done && (
                    <div className="flex flex-shrink-0 gap-1.5 sm:gap-2">
                        <button
                            onClick={handleAllow}
                            disabled={subscribing}
                            className="whitespace-nowrap rounded-lg bg-black px-2.5 py-1.5 text-xs font-bold text-amber-300 transition-colors hover:bg-gray-900 disabled:opacity-60 sm:px-4 sm:text-sm"
                        >
                            {subscribing ? 'Łączenie...' : '🔔 Włącz'}
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="whitespace-nowrap rounded-lg bg-amber-600 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700 sm:px-3 sm:text-sm"
                        >
                            Nie teraz
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PushConsent;
